import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

export default function ResetPassword() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionExists, setSessionExists] = useState(false);

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setSessionExists(true);
      } else {
        toast({ title: '⚠️ Sessão inválida', description: 'Use o link do e-mail para redefinir sua senha.' });
        navigate('/login', { replace: true });
      }
    };

    checkSession();
  }, [navigate, toast]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({ title: '❌ Erro', description: 'As senhas não coincidem.' });
      return;
    }

    if (password.length < 6) {
      toast({ title: '❌ Erro', description: 'A senha deve ter no mínimo 6 caracteres.' });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        toast({ title: '❌ Erro', description: error.message });
      } else {
        toast({ title: '✅ Sucesso', description: 'Senha redefinida com sucesso!' });
        navigate('/login', { replace: true });
      }
    } catch (err: any) {
      toast({ title: '❌ Erro', description: err?.message || 'Erro ao redefinir senha.' });
    } finally {
      setLoading(false);
    }
  };

  if (!sessionExists) {
    return null;
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-[#1a1f2e]">
      <div className="w-full max-w-md px-6">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#3d8a7d] to-[#4db8a8] rounded-xl mb-4 shadow-lg">
            <img 
              src="https://i.postimg.cc/SK6PNf6P/Assistente-financeiro-(150-x-150-px).png" 
              alt="Grana em Dia" 
              className="w-12 h-12 rounded-lg"
            />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Grana em <span className="text-[#f5f5c6]">Dia</span>
          </h1>
          <p className="text-gray-400 text-sm">Defina sua nova senha</p>
        </div>

        {/* Form Card */}
        <div className="bg-[#1e2538] p-8 rounded-xl shadow-2xl border border-gray-700">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-[#4db8a8] mb-2">🔐 Nova Senha</h2>
            <p className="text-gray-400 text-sm">Digite e confirme sua nova senha abaixo</p>
          </div>

          <form onSubmit={handleResetPassword} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nova Senha
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                className="w-full px-4 py-3 bg-[#0f172a] border border-gray-600 text-white rounded-lg 
                         focus:outline-none focus:ring-2 focus:ring-[#4db8a8] focus:border-transparent
                         placeholder-gray-500 transition-all"
                required
                minLength={6}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Confirmar Senha
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Digite a senha novamente"
                className="w-full px-4 py-3 bg-[#0f172a] border border-gray-600 text-white rounded-lg 
                         focus:outline-none focus:ring-2 focus:ring-[#4db8a8] focus:border-transparent
                         placeholder-gray-500 transition-all"
                required
                minLength={6}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#3d8a7d] to-[#4db8a8] text-white py-3 px-4 
                       rounded-lg font-semibold shadow-lg hover:shadow-xl 
                       hover:from-[#4db8a8] hover:to-[#3d8a7d] 
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all duration-200 transform hover:scale-[1.02]"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processando...
                </span>
              ) : (
                '🔄 Redefinir Senha'
              )}
            </button>
          </form>

          {/* Info */}
          <div className="mt-6 p-4 bg-[#fbbf24]/10 border border-[#fbbf24]/30 rounded-lg">
            <p className="text-sm text-[#fbbf24] flex items-start gap-2">
              <span className="text-lg">⚠️</span>
              <span>Após redefinir a senha, você será redirecionado para a página de login.</span>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/login')}
            className="text-gray-400 hover:text-[#4db8a8] text-sm transition-colors"
          >
            ← Voltar para o login
          </button>
        </div>
      </div>
    </div>
  );
}